import { useState, useEffect } from "react";
import axiosInstance from "@/config/axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import {
  Plus, Play, TestTube, Edit, Trash2, Power, PowerOff,
  Sparkles, CheckCircle, AlertCircle, Clock, TrendingUp
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CONDITION_TYPES = [
  { value: "sender", label: "Specific Sender" },
  { value: "sender_domain", label: "Sender Domain" },
  { value: "sender_contains", label: "Sender Contains" },
  { value: "subject_contains", label: "Subject Contains" },
  { value: "body_contains", label: "Body Contains" },
  { value: "subject_or_body_contains", label: "Subject OR Body Contains" },
  { value: "has_attachment", label: "Has Attachment" },
  { value: "older_than_days", label: "Older Than Days" },
  { value: "date_range", label: "Date Range" },
  { value: "is_unread", label: "Is Unread" },
  { value: "is_read", label: "Is Read" },
  { value: "has_tag", label: "Has Tag" },
  { value: "no_tags", label: "No Tags" },
  { value: "category", label: "AI Category" },
];

const ACTION_TYPES = [
  { value: "apply_tag", label: "Apply Tag", requiresValue: true },
  { value: "remove_tag", label: "Remove Tag", requiresValue: true },
  { value: "delete", label: "Delete Email", requiresValue: false },
  { value: "archive", label: "Archive Email", requiresValue: false },
  { value: "mark_read", label: "Mark as Read", requiresValue: false },
  { value: "mark_unread", label: "Mark as Unread", requiresValue: false },
  { value: "mark_important", label: "Mark as Important", requiresValue: false },
  { value: "create_ai_task", label: "Create AI Task", requiresValue: true },
  { value: "auto_draft_reply", label: "🤖 Auto-Draft Reply", requiresValue: true, isDraft: true },
];

const TAG_COLORS = ["red", "orange", "yellow", "green", "blue", "purple", "pink", "gray"];

export default function AutomationRulesManager({ actorId }) {
  const [rules, setRules] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [customCategories, setCustomCategories] = useState([]);
  const [suggestions, setSuggestions] = useState([]);

  const [formData, setFormData] = useState({
    name: "",
    description: "",
    conditions: [{ type: "sender", value: "", operator: "AND" }],
    actions: [{ type: "apply_tag", value: "", metadata: {} }],
    logical_operator: "AND",
    active: true,
    priority: "medium",
    apply_to_future: true,
    apply_to_existing: false,
    apply_to_server: true,
    new_emails_only: true,  // Default: only process new emails
  });

  useEffect(() => {
    fetchRules();
    fetchTemplates();
    fetchCustomCategories();
    fetchSuggestions();
  }, [actorId]);

  const fetchCustomCategories = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axiosInstance.get(
        `${API}/admin/ai-email/settings/${actorId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const categories = response.data?.analysis_custom_categories || ["Promotional", "Newsletter", "Personal", "Work"];
      setCustomCategories(categories);
    } catch (error) {
      console.error("Failed to fetch custom categories", error);
      setCustomCategories(["Promotional", "Newsletter", "Personal", "Work"]);
    }
  };

  const fetchRules = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      const response = await axiosInstance.get(
        `${API}/admin/ai-email/rules-v2/${actorId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setRules(response.data.rules || []);
    } catch (error) {
      console.error("Failed to fetch rules", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTemplates = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axiosInstance.get(
        `${API}/admin/ai-email/rule-templates`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setTemplates(response.data.templates || []);
    } catch (error) {
      console.error("Failed to fetch templates", error);
    }
  };

  const fetchSuggestions = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axiosInstance.get(
        `${API}/admin/ai-email/rule-suggestions/${actorId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSuggestions(response.data.suggestions || []);
    } catch (error) {
      console.error("Failed to fetch suggestions", error);
    }
  };

  const createRule = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axiosInstance.post(
        `${API}/admin/ai-email/rules-v2/${actorId}`,
        formData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const ruleId = response.data.rule_id;
      const applyingToExisting = response.data.applying_to_existing;
      
      if (applyingToExisting) {
        toast.success("Rule created! Applying to existing emails in background...");
        // Start polling for status
        pollRuleStatus(ruleId);
      } else {
        toast.success("Rule created successfully");
      }
      
      setShowCreateForm(false);
      resetForm();
      fetchRules();
    } catch (error) {
      console.error("Failed to create rule", error);
      toast.error("Failed to create rule");
    }
  };

  const updateRule = async () => {
    try {
      const token = localStorage.getItem("token");
      await axiosInstance.put(
        `${API}/admin/ai-email/rules-v2/${editingRule.id}`,
        formData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (formData.apply_to_existing) {
        toast.success("Rule updated! Applying to existing emails in background...");
        // Start polling for status
        pollRuleStatus(editingRule.id);
      } else {
        toast.success("Rule updated successfully");
      }
      
      setEditingRule(null);
      resetForm();
      fetchRules();
    } catch (error) {
      console.error("Failed to update rule", error);
      toast.error("Failed to update rule");
    }
  };

  const deleteRule = async (ruleId) => {
    if (!window.confirm("Are you sure you want to delete this rule?")) return;

    try {
      const token = localStorage.getItem("token");
      await axiosInstance.delete(
        `${API}/admin/ai-email/rules-v2/${ruleId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Rule deleted");
      fetchRules();
    } catch (error) {
      console.error("Failed to delete rule", error);
      toast.error("Failed to delete rule");
    }
  };

  const toggleRule = async (ruleId, active) => {
    try {
      const token = localStorage.getItem("token");
      await axiosInstance.patch(
        `${API}/admin/ai-email/rules-v2/${ruleId}/toggle?active=${active}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Rule ${active ? "activated" : "deactivated"}`);
      fetchRules();
    } catch (error) {
      console.error("Failed to toggle rule", error);
      toast.error("Failed to toggle rule");
    }
  };

  const runRule = async (ruleId) => {
    try {
      const token = localStorage.getItem("token");
      await axiosInstance.post(
        `${API}/admin/ai-email/rules-v2/${ruleId}/run?actor_id=${actorId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Rule execution started in background");
      
      // Start polling for status updates
      pollRuleStatus(ruleId);
    } catch (error) {
      console.error("Failed to run rule", error);
      toast.error("Failed to run rule");
    }
  };

  const pollRuleStatus = async (ruleId) => {
    const token = localStorage.getItem("token");
    let attempts = 0;
    const maxAttempts = 60; // Poll for up to 60 seconds
    
    const checkStatus = async () => {
      try {
        const response = await axiosInstance.get(
          `${API}/admin/ai-email/rules-v2/${ruleId}/status`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        const status = response.data.status;
        const stats = response.data.statistics || {};
        
        if (status === "completed") {
          const processed = stats.total_processed || 0;
          const matched = stats.total_matched || 0;
          toast.success(`Rule completed! Processed ${processed} emails (${matched} matched)`);
          fetchRules(); // Refresh rules to show updated statistics
          return;
        } else if (status === "running") {
          attempts++;
          if (attempts < maxAttempts) {
            // Continue polling every 2 seconds
            setTimeout(checkStatus, 2000);
          } else {
            toast.info("Rule is still running in background");
            fetchRules();
          }
        }
      } catch (error) {
        console.error("Failed to check rule status", error);
        fetchRules();
      }
    };
    
    // Start checking after 2 seconds
    setTimeout(checkStatus, 2000);
  };

  const createFromTemplate = async (template) => {
    try {
      const token = localStorage.getItem("token");
      await axiosInstance.post(
        `${API}/admin/ai-email/rules-v2/from-template/${actorId}?template_id=${template.id}`,
        { name: template.name, description: template.description, active: true },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Rule created from template: ${template.name}`);
      setShowTemplates(false);
      fetchRules();
    } catch (error) {
      console.error("Failed to create from template", error);
      toast.error("Failed to create rule from template");
    }
  };

  const createRuleFromSuggestion = async (suggestion) => {
    try {
      const token = localStorage.getItem("token");
      const ruleData = {
        name: suggestion.rule_template.name,
        description: suggestion.rule_template.reasoning || "AI suggested rule",
        conditions: suggestion.rule_template.conditions,
        actions: suggestion.rule_template.actions,
        logical_operator: "AND",
        active: true,
        priority: "medium",
        apply_to_future: true,
        apply_to_existing: false,
        apply_to_server: true,
        new_emails_only: true,
      };
      
      await axiosInstance.post(
        `${API}/admin/ai-email/rules-v2/${actorId}`,
        ruleData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`Rule created: ${suggestion.rule_template.name}`);
      fetchRules();
    } catch (error) {
      console.error("Failed to create rule from suggestion", error);
      toast.error("Failed to create rule from suggestion");
    }
  };

  const resetForm = () => {
    setFormData({
      name: "",
      description: "",
      conditions: [{ type: "sender", value: "", operator: "AND" }],
      actions: [{ type: "apply_tag", value: "", metadata: {} }],
      logical_operator: "AND",
      active: true,
      priority: "medium",
      apply_to_future: true,
      apply_to_existing: false,
      apply_to_server: true,
    });
    setShowCreateForm(false);
    setEditingRule(null);
  };

  const addCondition = () => {
    setFormData({
      ...formData,
      conditions: [...formData.conditions, { type: "sender", value: "", operator: "AND" }]
    });
  };

  const removeCondition = (index) => {
    setFormData({
      ...formData,
      conditions: formData.conditions.filter((_, i) => i !== index)
    });
  };

  const updateCondition = (index, field, value) => {
    const newConditions = [...formData.conditions];
    newConditions[index] = { ...newConditions[index], [field]: value };
    setFormData({ ...formData, conditions: newConditions });
  };

  const addAction = () => {
    setFormData({
      ...formData,
      actions: [...formData.actions, { type: "apply_tag", value: "", metadata: {} }]
    });
  };

  const removeAction = (index) => {
    setFormData({
      ...formData,
      actions: formData.actions.filter((_, i) => i !== index)
    });
  };

  const updateAction = (index, field, value) => {
    const newActions = [...formData.actions];
    newActions[index] = { ...newActions[index], [field]: value };
    setFormData({ ...formData, actions: newActions });
  };

  if (loading) {
    return <div className="text-center py-8 text-muted-foreground">Loading automation rules...</div>;
  }

  return (
    <div className="space-y-4">
      {/* Action Buttons */}
      <div className="flex justify-between items-center">
        <div className="text-sm text-muted-foreground">
          {rules.length} rule{rules.length !== 1 ? 's' : ''} •{' '}
          {rules.filter(r => r.active).length} active
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowTemplates(!showTemplates)}
          >
            <Sparkles className="w-4 h-4 mr-2" />
            Templates
          </Button>
          <Button
            size="sm"
            onClick={() => {
              setShowCreateForm(!showCreateForm);
              setEditingRule(null);
            }}
          >
            <Plus className="w-4 h-4 mr-2" />
            Create Rule
          </Button>
        </div>
      </div>

      {/* Templates Section */}
      {showTemplates && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {templates.map((template) => (
            <Card
              key={template.id}
              className="cursor-pointer hover:bg-gray-50"
              onClick={() => createFromTemplate(template)}
            >
              <CardHeader className="pb-3">
                <div className="flex items-start gap-2">
                  <span className="text-xl">{template.icon}</span>
                  <div className="flex-1">
                    <CardTitle className="text-sm">{template.name}</CardTitle>
                    <CardDescription className="text-xs">{template.description}</CardDescription>
                    <Badge variant="secondary" className="mt-2 text-xs">{template.category}</Badge>
                  </div>
                </div>
              </CardHeader>
            </Card>
          ))}
        </div>
      )}

      {/* Create/Edit Form - Compact Version */}
      {(showCreateForm || editingRule) && (
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base">{editingRule ? "Edit Rule" : "Create Rule"}</CardTitle>
              <Button variant="ghost" size="sm" onClick={resetForm}>Close</Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            <Input
              placeholder="Rule name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            />
            
            <Textarea
              placeholder="Description (optional)"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={2}
            />

            {/* Conditions */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium">Conditions</label>
                {formData.conditions.length > 1 && (
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-muted-foreground">Match:</span>
                    <Select
                      value={formData.logical_operator}
                      onValueChange={(value) => setFormData({ ...formData, logical_operator: value })}
                    >
                      <SelectTrigger className="w-20 h-7 text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="AND">ALL</SelectItem>
                        <SelectItem value="OR">ANY</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
              {formData.conditions.map((cond, idx) => (
                <div key={idx} className="flex gap-2">
                  <Select
                    value={cond.type}
                    onValueChange={(value) => updateCondition(idx, "type", value)}
                  >
                    <SelectTrigger className="w-48">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CONDITION_TYPES.map((t) => (
                        <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {!["is_unread", "is_read", "no_tags"].includes(cond.type) && (
                    cond.type === "category" ? (
                      <Select
                        value={cond.value || ""}
                        onValueChange={(value) => updateCondition(idx, "value", value)}
                      >
                        <SelectTrigger className="flex-1">
                          <SelectValue placeholder="Select category" />
                        </SelectTrigger>
                        <SelectContent>
                          {customCategories.map((category) => (
                            <SelectItem key={category} value={category}>
                              {category}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <Input
                        placeholder="Value"
                        value={cond.value || ""}
                        onChange={(e) => updateCondition(idx, "value", e.target.value)}
                      />
                    )
                  )}
                  {formData.conditions.length > 1 && (
                    <Button variant="ghost" size="sm" onClick={() => removeCondition(idx)}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              ))}
              <Button variant="outline" size="sm" onClick={addCondition}>
                <Plus className="w-3 h-3 mr-1" /> Add Condition
              </Button>
            </div>

            {/* Actions */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Actions</label>
              {formData.actions.map((action, idx) => (
                <div key={idx} className="flex gap-2">
                  <Select
                    value={action.type}
                    onValueChange={(value) => updateAction(idx, "type", value)}
                  >
                    <SelectTrigger className="w-48">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {ACTION_TYPES.map((t) => (
                        <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {ACTION_TYPES.find(t => t.value === action.type)?.requiresValue && (
                    <div className="space-y-2 w-full">
                      {action.type === "auto_draft_reply" ? (
                        <>
                          <label className="text-sm font-medium text-gray-700">
                            📝 Draft Instructions (for AI)
                          </label>
                          <Textarea
                            value={action.draft_instructions || action.value || ""}
                            onChange={(e) => {
                              updateAction(idx, "draft_instructions", e.target.value);
                              updateAction(idx, "value", e.target.value);
                            }}
                            placeholder="E.g., Draft a friendly response offering my availability. Always include: I'm available Mon-Fri 9AM-5PM EST. Ask them to suggest 2-3 time slots."
                            rows={4}
                            className="w-full"
                          />
                          <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-md">
                            <Checkbox
                              checked={action.auto_send || false}
                              onCheckedChange={(checked) => updateAction(idx, "auto_send", checked)}
                            />
                            <div>
                              <label className="text-sm font-medium text-amber-900">
                                Auto-send without review
                              </label>
                              <p className="text-xs text-amber-700">
                                ⚠️ Use with caution! Draft will be sent immediately without your approval.
                              </p>
                            </div>
                          </div>
                          <div className="p-3 bg-blue-50 border border-blue-200 rounded-md">
                            <p className="text-xs text-blue-800">
                              💡 <strong>Tip:</strong> Your personal drafting conditions will automatically be applied to this auto-draft.
                            </p>
                          </div>
                        </>
                      ) : action.type === "create_ai_task" ? (
                        <>
                          <label className="text-sm font-medium text-gray-700">
                            Task Instructions
                          </label>
                          <Textarea
                            value={action.value || ""}
                            onChange={(e) => updateAction(idx, "value", e.target.value)}
                            placeholder="E.g., Review this email and suggest a response"
                            rows={3}
                          />
                        </>
                      ) : (
                        <>
                          <Input
                            placeholder="Value"
                            value={action.value || ""}
                            onChange={(e) => updateAction(idx, "value", e.target.value)}
                          />
                          {action.type === "apply_tag" && (
                            <div className="flex gap-2 items-center">
                              <label className="text-sm">Color:</label>
                              {TAG_COLORS.map((color) => (
                                <button
                                  key={color}
                                  className={`w-6 h-6 rounded-full border-2 ${
                                    action.metadata?.color === color ? "border-black" : "border-gray-300"
                                  }`}
                                  style={{ backgroundColor: color }}
                                  onClick={() => updateAction(idx, "metadata", { ...action.metadata, color })}
                                />
                              ))}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  )}
                  {formData.actions.length > 1 && (
                    <Button variant="ghost" size="sm" onClick={() => removeAction(idx)}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              ))}
              <Button variant="outline" size="sm" onClick={addAction}>
                <Plus className="w-3 h-3 mr-1" /> Add Action
              </Button>
            </div>

            {/* Settings */}
            <div className="space-y-3 pt-2 border-t">
              <div className="flex items-center gap-2">
                <Checkbox
                  checked={formData.apply_to_existing}
                  onCheckedChange={(checked) => setFormData({ ...formData, apply_to_existing: checked })}
                />
                <label className="text-sm">Apply to existing emails now (one-time)</label>
              </div>
              
              <div className="flex items-center gap-2">
                <Checkbox
                  checked={formData.new_emails_only}
                  onCheckedChange={(checked) => setFormData({ ...formData, new_emails_only: checked })}
                />
                <div className="flex flex-col">
                  <label className="text-sm font-medium">Process new emails only</label>
                  <span className="text-xs text-muted-foreground">
                    If checked, only applies to emails not yet processed. Recommended for performance.
                  </span>
                </div>
              </div>
            </div>

            <div className="flex gap-2">
              <Button onClick={editingRule ? updateRule : createRule} size="sm">
                {editingRule ? "Update" : "Create"} Rule
              </Button>
              <Button variant="outline" size="sm" onClick={resetForm}>Cancel</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* AI Suggestions */}
      {suggestions.length > 0 && (
        <Card className="border-purple-200 bg-purple-50 mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-purple-600" />
              AI Suggested Rules
            </CardTitle>
            <CardDescription>
              Based on your email patterns, we recommend these automation rules
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {suggestions.slice(0, 3).map((suggestion, idx) => (
                <div key={idx} className="flex items-center justify-between p-3 bg-white rounded border">
                  <div className="flex-1">
                    <p className="font-medium">{suggestion.rule_template.name}</p>
                    <p className="text-sm text-muted-foreground">{suggestion.rule_template.reasoning}</p>
                    <Badge variant="secondary" className="mt-1">
                      {Math.round(suggestion.confidence * 100)}% confidence
                    </Badge>
                  </div>
                  <Button
                    size="sm"
                    onClick={() => createRuleFromSuggestion(suggestion)}
                  >
                    Create Rule
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Rules List */}
      {rules.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground">
          <p>No automation rules yet. Click "Create Rule" or choose a template to get started.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {rules.map((rule) => (
            <Card key={rule.id} className={!rule.active ? "opacity-60" : ""}>
              <CardContent className="pt-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <p className="font-medium text-sm">{rule.name}</p>
                      {rule.active ? (
                        <Badge variant="default" className="bg-green-500 text-xs">Active</Badge>
                      ) : (
                        <Badge variant="secondary" className="text-xs">Inactive</Badge>
                      )}
                    </div>
                    {rule.description && (
                      <p className="text-xs text-muted-foreground mb-2">{rule.description}</p>
                    )}
                    <div className="flex flex-wrap gap-2 text-xs">
                      <div>
                        <span className="font-medium">When:</span>{' '}
                        {rule.conditions?.map((c, i) => (
                          <Badge key={i} variant="outline" className="mr-1 text-xs">
                            {CONDITION_TYPES.find(t => t.value === c.type)?.label} 
                            {c.value && `: ${c.value}`}
                          </Badge>
                        ))}
                      </div>
                      <div>
                        <span className="font-medium">Then:</span>{' '}
                        {rule.actions?.map((a, i) => (
                          <Badge key={i} variant="default" className="mr-1 text-xs">
                            {ACTION_TYPES.find(t => t.value === a.type)?.label}
                            {a.value && `: ${a.value}`}
                          </Badge>
                        ))}
                      </div>
                    </div>
                    {rule.statistics && (
                      <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
                        <span>✓ {rule.statistics.total_processed || 0} processed</span>
                        {rule.statistics.last_run && (
                          <span>Last: {new Date(rule.statistics.last_run).toLocaleDateString()}</span>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="flex gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => toggleRule(rule.id, !rule.active)}
                      title={rule.active ? "Deactivate" : "Activate"}
                    >
                      {rule.active ? <PowerOff className="w-4 h-4" /> : <Power className="w-4 h-4" />}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => runRule(rule.id)}
                      title="Run now"
                    >
                      <Play className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setEditingRule(rule);
                        setFormData(rule);
                      }}
                      title="Edit"
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => deleteRule(rule.id)}
                      title="Delete"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
